import React from 'react';
import type { IJoditEditorProps } from '@shared/core/jodit-react-ssr';
import { Jodit } from '@shared/core/jodit-react-ssr';
import ReactDOM from 'react-dom';
import { HeadingButton } from '../../RichTextEditor.types';
import LinkForm, { LinkFormTranslations } from './components/LinkForm';
import { DATA_JOY_DATA } from './constants';
import { CustomLinkPluginOptions } from './CustomLink';
import { getLink } from './link';

export interface LinkPopupRef {
  setLoading: (val: boolean) => void;
  close: () => void;
}

export const linkPopupRefDefaultValue: LinkPopupRef = {
  setLoading: () => {},
  close: () => {},
};

type CreateLinkPopupParams = Readonly<{
  editor: Jodit;
  close: () => void;
  ref: React.RefObject<LinkPopupRef>;
  translations: LinkFormTranslations;
  isUpdate?: boolean;
}>;

type JoditConfigWithCustomLink = Readonly<
  IJoditEditorProps['config'] & {
    customLinkPlugin: CustomLinkPluginOptions;
  }
>;

/**
 * Create the component to be rendered when the link icon is clicked in the toolbar or via inline toolbar
 * @param param0 { editor, close, ref, translations, isUpdate }: CreateLinkPopupParams
 *  @returns { HTMLDivElement } The container element with the rendered LinkForm
 */
const createLinkPopup = ({ editor, close, ref, translations, isUpdate = false }: CreateLinkPopupParams) => {
  const options = (editor.o as unknown as JoditConfigWithCustomLink).customLinkPlugin;
  const root = document.createElement('div');
  editor.s.save();

  const closePopup = () => {
    close();
    ReactDOM.unmountComponentAtNode(root);
  };

  // Migrate ReactDOM.render to ReactDOM.createRoot on upgrading to React 18
  ReactDOM.render(
    <LinkForm
      onSubmit={(e, url, content, setLoading) => {
        editor.s.restore();
        if (ref.current) {
          ref.current.setLoading = setLoading;
          ref.current.close = closePopup;
        }
        const link = getLink(editor);
        const data = link ? link.getAttribute(DATA_JOY_DATA) || '' : undefined;
        options.onAddLink(editor, url, content, data);
        e.preventDefault();
      }}
      isUpdate={isUpdate}
      jodit={editor}
      {...(isUpdate
        ? {
            onUnlink: () => {
              editor.execCommand('unlink');
              closePopup();
            },
          }
        : {})}
      translations={translations}
    />,
    root,
  );

  return root;
};

export const getCustomLinkPluginControls = (translations: LinkFormTranslations, ref: React.RefObject<LinkPopupRef>) => {
  return {
    paragraph: {
      update: (_: Jodit, button: HeadingButton) => {
        const control = button.control;
        const currentValue = button.state.value;
        const list = control.list;
        button.state.text = list[currentValue.toString()].toString();
      },
    },
    insertLink: {
      tooltip: 'Insert Link',
      icon: 'link',
      popup: (editor: Jodit, _: Nullable<Node>, close: () => void) => createLinkPopup({ editor, close, ref, translations, isUpdate: false }),
    },
    updateLink: {
      tooltip: 'Insert Link',
      icon: 'pencil',
      popup: (editor: Jodit, _: Nullable<Node>, close: () => void) => createLinkPopup({ editor, close, ref, translations, isUpdate: true }),
    },
  };
};
